// src/pages/customer-panel/personalize/PersonalizeWebsitePage.js
// Employee Website Personalization Page - Full scrape/train workflow

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    Button,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    Snackbar,
    Tooltip,
    Checkbox,
    Tabs,
    Tab,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    LinearProgress,
    Collapse,
    Divider,
    Switch,
    FormControlLabel,
    Radio,
    RadioGroup,
    InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import LanguageIcon from '@mui/icons-material/Language';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MapIcon from '@mui/icons-material/Map';
import HomeIcon from '@mui/icons-material/Home';
import ListIcon from '@mui/icons-material/List';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import SaveIcon from '@mui/icons-material/Save';
import SyncIcon from '@mui/icons-material/Sync';
import LinkIcon from '@mui/icons-material/Link';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';

const STATUS_CONFIG = {
    pending: { color: '#f59e0b', bg: '#fef3c7', label: 'Pending' },
    success: { color: '#10b981', bg: '#d1fae5', label: 'Scraped' },
    error: { color: '#ef4444', bg: '#fee2e2', label: 'Error' },
    trained: { color: '#3b82f6', bg: '#dbeafe', label: 'Trained' },
    update_pending: { color: '#f97316', bg: '#ffedd5', label: 'Update Pending' },
};

const TAB_CONFIG = [
    { value: 'untrain', label: 'Untrain Data' },
    { value: 'error', label: 'Error Data' },
    { value: 'trained', label: 'Trained Data' },
    { value: 'archived', label: 'Archive Data' },
];

const MAX_BATCH_SIZE = 50;

const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    try {
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
        });
    } catch { return '--'; }
};

const formatDateTime = (dateStr) => {
    if (!dateStr) return '--';
    try {
        return new Date(dateStr).toLocaleString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    } catch { return '--'; }
};

// --- Markdown to HTML converter ---
const convertMarkdownToHtml = (markdown) => {
    if (!markdown) return '';
    let html = markdown;
    html = html.replace(/([^\n])(#{1,6}\s)/g, '$1\n\n$2');
    html = html.replace(/^######\s+(.*$)/gim, '<h6>$1</h6>');
    html = html.replace(/^#####\s+(.*$)/gim, '<h5>$1</h5>');
    html = html.replace(/^####\s+(.*$)/gim, '<h4>$1</h4>');
    html = html.replace(/^###\s+(.*$)/gim, '<h3>$1</h3>');
    html = html.replace(/^##\s+(.*$)/gim, '<h2>$1</h2>');
    html = html.replace(/^#\s+(.*$)/gim, '<h1>$1</h1>');
    html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/gim, '<img src="$2" alt="$1" style="max-width:100%;height:auto;" />');
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color:#3b82f6;text-decoration:underline;">$1</a>');
    html = html.replace(/\*\*([^*]+)\*\*/gim, '<strong>$1</strong>');
    html = html.replace(/__([^_]+)__/gim, '<strong>$1</strong>');
    html = html.replace(/\*([^*]+)\*/gim, '<em>$1</em>');
    html = html.replace(/_([^_]+)_/gim, '<em>$1</em>');
    html = html.replace(/```([^`]+)```/gim, '<pre><code>$1</code></pre>');
    html = html.replace(/`([^`]+)`/gim, '<code>$1</code>');
    html = html.replace(/^\* (.+)$/gim, '<li>$1</li>');
    html = html.replace(/^- (.+)$/gim, '<li>$1</li>');
    html = html.replace(/^\d+\. (.+)$/gim, '<li>$1</li>');
    html = html.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');
    html = html.replace(/\n\n/g, '</p><p>');
    html = html.replace(/\n/g, '<br />');
    if (!html.startsWith('<h') && !html.startsWith('<p') && !html.startsWith('<ul') && !html.startsWith('<ol')) {
        html = '<p>' + html + '</p>';
    }
    return html;
};

// --- Expanded Row for Trained Data ---
function ExpandedRow({ link }) {
    const summaryHtml = useMemo(() => convertMarkdownToHtml(link.summary), [link.summary]);
    const contentHtml = useMemo(() => convertMarkdownToHtml(link.page_content), [link.page_content]);

    return (
        <Box sx={{ p: 3, bgcolor: '#f8fafc' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
                <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinkIcon sx={{ fontSize: 16, color: GREEN_ACCENT }} /> Website Link
                    </Typography>
                    <Typography
                        component="a" href={link.website_link} target="_blank" rel="noopener noreferrer"
                        sx={{ fontFamily, color: GREEN_ACCENT, fontSize: '0.85rem', wordBreak: 'break-all', '&:hover': { textDecoration: 'underline' } }}
                    >
                        {link.website_link}
                    </Typography>
                </Box>
                <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 0.5 }}>Title Tag</Typography>
                    <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>{link.title_tag || 'N/A'}</Typography>
                </Box>
                <Box>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarTodayIcon sx={{ fontSize: 16 }} /> Timestamps
                    </Typography>
                    <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem' }}>Created: {formatDateTime(link.created_date)}</Typography>
                    <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem' }}>Updated: {formatDateTime(link.updated_date)}</Typography>
                </Box>
                <Box>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SyncIcon sx={{ fontSize: 16 }} /> Sync Settings
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem' }}>Auto Sync:</Typography>
                        <Chip
                            label={link.is_auto_sync ? 'On' : 'Off'}
                            size="small"
                            sx={{
                                fontFamily, fontWeight: 600, fontSize: '0.7rem',
                                bgcolor: link.is_auto_sync ? '#d1fae5' : '#f1f5f9',
                                color: link.is_auto_sync ? '#059669' : '#64748b',
                            }}
                        />
                    </Box>
                    {link.is_auto_sync && (
                        <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.75rem', mt: 0.5 }}>
                            Interval: {link.sync_interval_days || link.default_interval_days || 7} days
                        </Typography>
                    )}
                </Box>
                <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 1 }}>Summary</Typography>
                    <Box
                        sx={{ p: 2, borderRadius: '10px', bgcolor: '#fff', border: '1px solid #e2e8f0', maxHeight: 120, overflowY: 'auto', fontSize: '0.8rem', color: '#475569', '& h1,& h2,& h3,& h4': { fontSize: '1rem', fontWeight: 700, my: 0.5 } }}
                        dangerouslySetInnerHTML={{ __html: summaryHtml || '<p>No summary available</p>' }}
                    />
                </Box>
                <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 1 }}>Page Content</Typography>
                    <Box
                        sx={{ p: 2, borderRadius: '10px', bgcolor: '#fff', border: '1px solid #e2e8f0', maxHeight: 220, overflowY: 'auto', fontSize: '0.8rem', color: '#475569', '& h1,& h2,& h3,& h4': { fontSize: '1rem', fontWeight: 700, my: 0.5 } }}
                        dangerouslySetInnerHTML={{ __html: contentHtml || '<p>No content</p>' }}
                    />
                </Box>
            </Box>
        </Box>
    );
}

// --- Add Website Link Dialog ---
function AddLinkDialog({ open, onClose, onSuccess, showSnackbar }) {
    const [url, setUrl] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!url.trim()) { showSnackbar('Please enter a URL.', 'warning'); return; }
        try { new URL(url); } catch { showSnackbar('Please enter a valid URL (e.g., https://example.com)', 'error'); return; }
        setSubmitting(true);
        try {
            await axiosInstance.post('/employee-website/links', { website_link: url.trim() });
            showSnackbar('Website link added successfully!', 'success');
            setUrl('');
            onSuccess();
            onClose();
        } catch (err) {
            showSnackbar(err.response?.data?.message || 'Failed to add link.', 'error');
        } finally { setSubmitting(false); }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Add Website Link</DialogTitle>
            <DialogContent>
                <TextField
                    label="Website URL" fullWidth placeholder="https://example.com/page" value={url}
                    onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
                    helperText="Enter a single webpage URL to scrape and train" sx={{ mt: 1, '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily }, '& .MuiFormHelperText-root': { fontFamily } }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={onClose} sx={{ fontFamily, fontWeight: 600, color: '#64748b', textTransform: 'none' }}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
                    sx={{ fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '10px', textTransform: 'none', '&:hover': { bgcolor: '#059669' } }}>
                    Add Link
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// --- Bulk Add Links Dialog ---
function BulkAddDialog({ open, onClose, onSuccess, showSnackbar }) {
    const [linksText, setLinksText] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => setLinksText(ev.target?.result || '');
        reader.readAsText(file);
        e.target.value = '';
    };

    const handleSubmit = async () => {
        if (!linksText.trim()) { showSnackbar('Please enter at least one URL.', 'warning'); return; }
        const links = linksText.split(/[\n,\s]+/).map(l => l.trim()).filter(l => l.length > 0);
        if (links.length === 0) { showSnackbar('No valid URLs found.', 'error'); return; }
        const invalid = links.filter(l => { try { new URL(l); return false; } catch { return true; } });
        if (invalid.length > 0) { showSnackbar(`Found ${invalid.length} invalid URL(s). Please check your input.`, 'error'); return; }
        setSubmitting(true);
        try {
            const res = await axiosInstance.post('/employee-website/links/bulk', { links });
            const d = res.data;
            if (d.added_count > 0) {
                showSnackbar(`Added ${d.added_count} link(s).${d.failed_count > 0 ? ` ${d.failed_count} failed.` : ''}`, 'success');
                setLinksText(''); onSuccess(); onClose();
            } else {
                showSnackbar('No links were added. Check for duplicates.', 'error');
            }
        } catch (err) { showSnackbar(err.response?.data?.message || 'Failed to add links.', 'error'); }
        finally { setSubmitting(false); }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Add Multiple Website Links</DialogTitle>
            <DialogContent>
                <TextField
                    label="Website URLs" fullWidth multiline rows={8} value={linksText}
                    onChange={(e) => setLinksText(e.target.value)}
                    placeholder={"Enter URLs (one per line or separated by commas)\nhttps://example.com/page1\nhttps://example.com/page2"}
                    helperText="Enter multiple URLs separated by new lines, commas, or spaces"
                    sx={{ mt: 1, '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily }, '& .MuiFormHelperText-root': { fontFamily } }}
                />
                <Button component="label" startIcon={<UploadFileIcon />} sx={{ fontFamily, fontWeight: 600, color: GREEN_ACCENT, textTransform: 'none', mt: 1 }}>
                    Or upload a text file
                    <input type="file" accept=".txt,.csv" onChange={handleFileUpload} hidden />
                </Button>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={onClose} sx={{ fontFamily, fontWeight: 600, color: '#64748b', textTransform: 'none' }}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
                    sx={{ fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '10px', textTransform: 'none', '&:hover': { bgcolor: '#059669' } }}>
                    Add All Links
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// --- Sitemap / Homepage / List Dialog ---
function SitemapDialog({ open, onClose, onSuccess, showSnackbar, mode }) {
    const [urlInput, setUrlInput] = useState('');
    const [sitemaps, setSitemaps] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [extractingId, setExtractingId] = useState(null);

    const loadSitemaps = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/employee-website/sitemaps');
            setSitemaps(res.data?.sitemaps || []);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { if (open) { loadSitemaps(); setUrlInput(''); } }, [open, loadSitemaps]);

    const handleAdd = async () => {
        if (!urlInput.trim()) { showSnackbar('Please enter a URL.', 'warning'); return; }
        try { new URL(urlInput); } catch { showSnackbar('Please enter a valid URL.', 'error'); return; }
        setSubmitting(true);
        try {
            await axiosInstance.post('/employee-website/sitemaps', { sitemap: urlInput.trim() });
            showSnackbar(mode === 'homepage' ? 'Homepage added!' : 'Sitemap added!', 'success');
            setUrlInput(''); loadSitemaps(); onSuccess();
        } catch (err) { showSnackbar(err.response?.data?.message || 'Failed to add.', 'error'); }
        finally { setSubmitting(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this item?')) return;
        try {
            await axiosInstance.delete(`/employee-website/sitemaps/${id}`);
            showSnackbar('Deleted!', 'success'); loadSitemaps();
        } catch (err) { showSnackbar(err.response?.data?.message || 'Failed to delete.', 'error'); }
    };

    const handleExtract = async (sitemap) => {
        setExtractingId(sitemap.id);
        try {
            const res = await axiosInstance.post('/employee-website/sitemaps/extract-links', { sitemap: sitemap.sitemap });
            const d = res.data;
            showSnackbar(`Extracted ${d.added_count} link(s).${d.failed_count > 0 ? ` ${d.failed_count} failed.` : ''}`, 'success');
            onSuccess();
            if (mode !== 'list') onClose();
        } catch (err) { showSnackbar(err.response?.data?.message || 'Extraction failed.', 'error'); }
        finally { setExtractingId(null); }
    };

    const title = mode === 'homepage' ? 'Crawl Homepage' : mode === 'list' ? 'Submitted Sitemaps' : 'Manage Sitemaps';
    const TitleIcon = mode === 'homepage' ? HomeIcon : mode === 'list' ? ListIcon : MapIcon;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px', maxHeight: '90vh' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                <TitleIcon sx={{ color: GREEN_ACCENT }} /> {title}
            </DialogTitle>
            <DialogContent>
                {mode !== 'list' && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ fontFamily, fontWeight: 600, mb: 1 }}>
                            {mode === 'homepage' ? 'Homepage URL' : 'Sitemap URL'} *
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                                fullWidth size="small" value={urlInput} onChange={(e) => setUrlInput(e.target.value)}
                                placeholder={mode === 'homepage' ? 'https://example.com' : 'https://example.com/sitemap.xml'}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
                                sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' } }}
                            />
                            <Button variant="contained" onClick={handleAdd} disabled={submitting}
                                sx={{ fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '10px', textTransform: 'none', minWidth: 80, '&:hover': { bgcolor: '#059669' } }}>
                                {submitting ? <CircularProgress size={20} color="inherit" /> : 'Add'}
                            </Button>
                        </Box>
                        <Typography variant="caption" sx={{ fontFamily, color: '#64748b', mt: 0.5 }}>
                            {mode === 'homepage' ? 'Enter the homepage URL to crawl for links.' : "Enter your website's sitemap URL to extract all pages."}
                        </Typography>
                    </Box>
                )}
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', mb: 2 }}>
                    Submitted URLs ({sitemaps.length})
                </Typography>
                {loading ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} sx={{ color: GREEN_ACCENT }} /></Box>
                ) : sitemaps.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4, bgcolor: '#f8fafc', borderRadius: '12px', border: '2px dashed #e2e8f0' }}>
                        <Typography sx={{ fontFamily, color: '#94a3b8' }}>No items added yet.</Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {sitemaps.map((s) => (
                            <Paper key={s.id} sx={{ p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography component="a" href={s.sitemap} target="_blank" rel="noopener noreferrer"
                                        sx={{ fontFamily, color: GREEN_ACCENT, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', '&:hover': { textDecoration: 'underline' } }}>
                                        {s.sitemap} <OpenInNewIcon sx={{ fontSize: 14 }} />
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>Added on {formatDate(s.created_date)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                                    <Button size="small" variant="contained" onClick={() => handleExtract(s)} disabled={extractingId === s.id}
                                        startIcon={extractingId === s.id ? <CircularProgress size={14} color="inherit" /> : null}
                                        sx={{ fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '8px', textTransform: 'none', fontSize: '0.75rem', '&:hover': { bgcolor: '#059669' } }}>
                                        {extractingId === s.id ? 'Extracting...' : 'Extract Links'}
                                    </Button>
                                    <IconButton size="small" onClick={() => handleDelete(s.id)} sx={{ color: '#ef4444' }}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={onClose} sx={{ fontFamily, fontWeight: 600, color: '#64748b', textTransform: 'none' }}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

// --- Edit Content Dialog ---
function EditContentDialog({ open, onClose, linkId, onSuccess, showSnackbar }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [link, setLink] = useState(null);
    const [content, setContent] = useState('');

    useEffect(() => {
        if (open && linkId) {
            setLoading(true);
            axiosInstance.get('/employee-website/links', { params: { page: 1, limit: 1000 } })
                .then(res => {
                    const found = (res.data?.links || []).find(l => l.id === linkId);
                    if (found) { setLink(found); setContent(found.page_content || ''); }
                    else { showSnackbar('Link not found.', 'error'); onClose(); }
                })
                .catch(() => { showSnackbar('Failed to load link data.', 'error'); onClose(); })
                .finally(() => setLoading(false));
        }
    }, [open, linkId, onClose, showSnackbar]);

    const handleSave = async () => {
        if (!content.trim()) { showSnackbar('Content cannot be empty.', 'warning'); return; }
        setSaving(true);
        try {
            await axiosInstance.put(`/employee-website/links/${linkId}/content`, { page_content: content });
            showSnackbar('Content updated!', 'success'); onSuccess(); onClose();
        } catch (err) { showSnackbar(err.response?.data?.message || 'Failed to save.', 'error'); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: '16px', maxHeight: '90vh' } }}>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                    <CircularProgress sx={{ color: GREEN_ACCENT }} />
                </Box>
            ) : (
                <>
                    <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                        <Box>Edit Website Content</Box>
                        <Typography variant="body2" sx={{ fontFamily, color: '#64748b', mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {link?.website_link}
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        {link?.has_pending_update && (
                            <Alert severity="warning" sx={{ mb: 2, fontFamily, borderRadius: '10px' }}>
                                This content has a pending update from a refresh. The current content shown here is your manually edited version.
                            </Alert>
                        )}
                        <TextField
                            fullWidth multiline rows={18} value={content} onChange={(e) => setContent(e.target.value)}
                            placeholder="Page content..."
                            helperText="Edit the content directly. Markdown formatting is preserved."
                            sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px', fontSize: '0.85rem' }, '& .MuiFormHelperText-root': { fontFamily } }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 3 }}>
                        <Button onClick={onClose} disabled={saving} sx={{ fontFamily, fontWeight: 600, color: '#64748b', textTransform: 'none' }}>Cancel</Button>
                        <Button variant="contained" onClick={handleSave} disabled={saving}
                            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                            sx={{ fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '10px', textTransform: 'none', '&:hover': { bgcolor: '#059669' } }}>
                            {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
}

// --- Sync Settings Dialog ---
function SyncSettingsDialog({ open, onClose, linkId, onSuccess, showSnackbar }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [link, setLink] = useState(null);
    const [isAutoSync, setIsAutoSync] = useState(false);
    const [useCustomInterval, setUseCustomInterval] = useState(false);
    const [syncInterval, setSyncInterval] = useState(7);

    useEffect(() => {
        if (open && linkId) {
            setLoading(true);
            axiosInstance.get('/employee-website/links', { params: { page: 1, limit: 1000 } })
                .then(res => {
                    const found = (res.data?.links || []).find(l => l.id === linkId);
                    if (found) {
                        setLink(found);
                        setIsAutoSync(found.is_auto_sync);
                        setUseCustomInterval(found.sync_interval_days !== null && found.sync_interval_days !== 0);
                        setSyncInterval(found.sync_interval_days || found.default_interval_days || 7);
                    } else { showSnackbar('Link not found.', 'error'); onClose(); }
                })
                .catch(() => { showSnackbar('Failed to load link data.', 'error'); onClose(); })
                .finally(() => setLoading(false));
        }
    }, [open, linkId, onClose, showSnackbar]);

    const handleSave = async () => {
        if (isAutoSync && useCustomInterval && (!syncInterval || syncInterval < 1)) {
            showSnackbar('Sync interval must be at least 1 day.', 'warning'); return;
        }
        setSaving(true);
        try {
            await axiosInstance.post('/employee-website/sync-settings', {
                updates: [{ id: linkId, is_auto_sync: isAutoSync, sync_interval_days: useCustomInterval ? Number(syncInterval) : null }]
            });
            showSnackbar('Sync settings updated!', 'success'); onSuccess(); onClose();
        } catch (err) { showSnackbar(err.response?.data?.message || 'Failed to save.', 'error'); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: GREEN_ACCENT }} /></Box>
            ) : (
                <>
                    <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                        <Box>Auto-Sync Settings</Box>
                        <Typography variant="body2" sx={{ fontFamily, color: '#64748b', mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {link?.website_link}
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <FormControlLabel
                            control={<Switch checked={isAutoSync} onChange={(e) => setIsAutoSync(e.target.checked)} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: GREEN_ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: GREEN_ACCENT } }} />}
                            label={<Box><Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.9rem' }}>Enable Auto-Sync</Typography><Typography variant="caption" sx={{ fontFamily, color: '#64748b' }}>Automatically check for updates and refresh content</Typography></Box>}
                            sx={{ mb: 2, alignItems: 'flex-start' }}
                        />
                        {isAutoSync && (
                            <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: '12px', mb: 2 }}>
                                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', mb: 1.5 }}>Sync Frequency</Typography>
                                <RadioGroup value={useCustomInterval ? 'custom' : 'default'} onChange={(e) => setUseCustomInterval(e.target.value === 'custom')}>
                                    <FormControlLabel value="default" control={<Radio size="small" sx={{ '&.Mui-checked': { color: GREEN_ACCENT } }} />}
                                        label={<Box><Typography sx={{ fontFamily, fontSize: '0.85rem' }}>Default interval ({link?.default_interval_days || 7} days)</Typography><Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>Recommended for most websites</Typography></Box>}
                                    />
                                    <FormControlLabel value="custom" control={<Radio size="small" sx={{ '&.Mui-checked': { color: GREEN_ACCENT } }} />}
                                        label={<Typography sx={{ fontFamily, fontSize: '0.85rem' }}>Custom interval</Typography>}
                                    />
                                </RadioGroup>
                                {useCustomInterval && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 4, mt: 1 }}>
                                        <TextField type="number" size="small" value={syncInterval} onChange={(e) => setSyncInterval(e.target.value)}
                                            inputProps={{ min: 1, max: 365 }}
                                            sx={{ width: 100, '& .MuiInputBase-root': { fontFamily, borderRadius: '8px' } }}
                                        />
                                        <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>days</Typography>
                                    </Box>
                                )}
                                {link?.last_sync_check_date && (
                                    <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', display: 'block', mt: 2, pt: 1.5, borderTop: '1px solid #e2e8f0' }}>
                                        Last sync check: {formatDateTime(link.last_sync_check_date)}
                                    </Typography>
                                )}
                                <Alert severity="info" sx={{ mt: 2, fontFamily, borderRadius: '10px', fontSize: '0.75rem' }}>
                                    The system will check for updates at the specified interval. If the content has changed, it will be refreshed. Manually edited content will get updates saved as "pending" for your review.
                                </Alert>
                            </Box>
                        )}
                        {link?.is_manually_edited && (
                            <Alert severity="warning" sx={{ fontFamily, borderRadius: '10px', fontSize: '0.75rem' }}>
                                This content has been manually edited. Auto-sync updates will be saved as "pending" to preserve your changes.
                            </Alert>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 3 }}>
                        <Button onClick={onClose} disabled={saving} sx={{ fontFamily, fontWeight: 600, color: '#64748b', textTransform: 'none' }}>Cancel</Button>
                        <Button variant="contained" onClick={handleSave} disabled={saving}
                            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                            sx={{ fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '10px', textTransform: 'none', '&:hover': { bgcolor: '#059669' } }}>
                            {saving ? 'Saving...' : 'Save Settings'}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
}

// --- Processing Modal ---
function ProcessingDialog({ open, onClose, files, action, showSnackbar }) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [processing, setProcessing] = useState(false);
    const [results, setResults] = useState([]);
    const [done, setDone] = useState(false);

    const actionLabel = { scrape: 'Scraping', train: 'Training', untrain: 'Untraining', archive: 'Archiving', retrain: 'Retraining' }[action] || 'Processing';

    const processNext = useCallback(async (index) => {
        if (index >= files.length) { setDone(true); setProcessing(false); return; }
        setCurrentIndex(index);
        const file = files[index];
        try {
            if (action === 'scrape') {
                await axiosInstance.post('/employee-website/scrape', { link_ids: [file.id] });
            } else if (action === 'train') {
                await axiosInstance.post('/employee-website/training-status', { link_ids: [file.id], is_trained: true });
            } else if (action === 'untrain') {
                await axiosInstance.post('/employee-website/training-status', { link_ids: [file.id], is_trained: false });
            } else if (action === 'archive') {
                await axiosInstance.post('/employee-website/archive', { link_ids: [file.id] });
            } else if (action === 'retrain') {
                await axiosInstance.post('/employee-website/retrain-archived', { link_ids: [file.id] });
            }
            setResults(prev => [...prev, { id: file.id, success: true }]);
        } catch {
            setResults(prev => [...prev, { id: file.id, success: false }]);
        }
        processNext(index + 1);
    }, [files, action]);

    useEffect(() => {
        if (open && files.length > 0 && !processing && !done) {
            setProcessing(true); setResults([]); setCurrentIndex(0); setDone(false);
            processNext(0);
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const successCount = results.filter(r => r.success).length;
    const failCount = results.filter(r => !r.success).length;
    const progress = files.length > 0 ? (results.length / files.length) * 100 : 0;

    return (
        <Dialog open={open} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>{actionLabel} Websites</DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem' }}>
                            {done ? 'Complete!' : `${actionLabel}...`}
                        </Typography>
                        <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>
                            {results.length} / {files.length}
                        </Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: GREEN_ACCENT, borderRadius: 4 } }} />
                </Box>
                {done && (
                    <Alert severity={failCount > 0 ? 'warning' : 'success'} sx={{ fontFamily, borderRadius: '10px' }}>
                        {successCount} succeeded{failCount > 0 ? `, ${failCount} failed` : ''}
                    </Alert>
                )}
                {!done && currentIndex < files.length && (
                    <Typography variant="body2" sx={{ fontFamily, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {files[currentIndex]?.website_link || files[currentIndex]?.name || `Link ${currentIndex + 1}`}
                    </Typography>
                )}
            </DialogContent>
            {done && (
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button variant="contained" onClick={onClose}
                        sx={{ fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '10px', textTransform: 'none', '&:hover': { bgcolor: '#059669' } }}>
                        Done
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
}

// ============= MAIN PAGE =============
export default function PersonalizeWebsitePage() {
    const [activeTab, setActiveTab] = useState('untrain');
    const [links, setLinks] = useState([]);
    const [totalLinks, setTotalLinks] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [selectedIds, setSelectedIds] = useState(new Set());
    const [expandedId, setExpandedId] = useState(null);

    // Dialogs
    const [addLinkOpen, setAddLinkOpen] = useState(false);
    const [bulkAddOpen, setBulkAddOpen] = useState(false);
    const [sitemapOpen, setSitemapOpen] = useState(false);
    const [sitemapMode, setSitemapMode] = useState('sitemap');
    const [editLinkId, setEditLinkId] = useState(null);
    const [syncLinkId, setSyncLinkId] = useState(null);
    const [processingModal, setProcessingModal] = useState({ open: false, files: [], action: 'train' });

    // Add menu
    const [addMenuAnchor, setAddMenuAnchor] = useState(null);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const showSnackbar = useCallback((message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    }, []);

    // Load data
    const loadLinks = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            let res;
            const params = { page: page + 1, limit: rowsPerPage, search: searchTerm };
            switch (activeTab) {
                case 'untrain':
                    res = await axiosInstance.get('/employee-website/links/untrain', { params, status: 'untrain' });
                    break;
                case 'error':
                    res = await axiosInstance.get('/employee-website/links', { params: { ...params, status: 'error' } });
                    break;
                case 'trained':
                    res = await axiosInstance.get('/employee-website/links/scraped-trained', { params, status: 'trained' });
                    break;
                case 'archived':
                    res = await axiosInstance.get('/employee-website/archived', { params, status: 'archived' });
                    break;
                default:
                    res = await axiosInstance.get('/employee-website/links', { params });
            }
            const data = res.data;
            setLinks(data.links || []);
            setTotalLinks(data.total || data.links?.length || 0);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load data.');
        } finally { setLoading(false); }
    }, [activeTab, page, rowsPerPage, searchTerm]);

    useEffect(() => {
        document.title = 'Personalize Websites | Customer Panel';
        loadLinks();
    }, [loadLinks]);

    // Row actions
    const handleDeleteLink = async (link) => {
        if (!window.confirm(`Delete "${link.website_link}"?`)) return;
        try {
            await axiosInstance.delete(`/employee-website/links/${link.id}`);
            showSnackbar('Link deleted!'); loadLinks();
        } catch (err) { showSnackbar(err.response?.data?.message || 'Failed to delete.', 'error'); }
    };

    const handleRefreshData = async (link) => {
        if (!window.confirm('Refresh scraped data from website?')) return;
        try {
            await axiosInstance.post('/employee-website/refresh', { id: link.id, link: link.website_link });
            showSnackbar('Data refreshed!'); loadLinks();
        } catch (err) { showSnackbar(err.response?.data?.message || 'Refresh failed.', 'error'); }
    };

    const handleRefreshSummary = async (link) => {
        if (!link.page_content) { showSnackbar('No content available to summarize.', 'warning'); return; }
        try {
            await axiosInstance.post(`/employee-website/links/${link.id}/refresh-summary`);
            showSnackbar('Summary refreshed!'); loadLinks();
        } catch (err) { showSnackbar(err.response?.data?.message || 'Failed to refresh.', 'error'); }
    };

    // Selection
    const handleSelectAll = (e) => {
        if (e.target.checked) { setSelectedIds(new Set(links.map(l => l.id))); }
        else { setSelectedIds(new Set()); }
    };
    const handleSelectRow = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const allSelected = links.length > 0 && links.every(l => selectedIds.has(l.id));
    const someSelected = links.some(l => selectedIds.has(l.id)) && !allSelected;

    // Bulk actions
    const getSelectedLinks = () => links.filter(l => selectedIds.has(l.id));

    const handleBulkAction = (action) => {
        const selected = getSelectedLinks();
        if (selected.length === 0) { showSnackbar('Select at least one link.', 'warning'); return; }
        if (selected.length > MAX_BATCH_SIZE) { showSnackbar(`Select ${MAX_BATCH_SIZE} or fewer links.`, 'warning'); return; }
        if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} ${selected.length} link(s)?`)) return;
        setProcessingModal({ open: true, files: selected, action });
    };

    const handleBulkDelete = async () => {
        const selected = getSelectedLinks();
        if (selected.length === 0) { showSnackbar('Select at least one link.', 'warning'); return; }
        if (!window.confirm(`Delete ${selected.length} link(s)? This cannot be undone.`)) return;
        try {
            for (const link of selected) { await axiosInstance.delete(`/employee-website/links/${link.id}`); }
            showSnackbar(`Deleted ${selected.length} link(s)!`);
            setSelectedIds(new Set()); loadLinks();
        } catch { showSnackbar('Failed to delete some links.', 'error'); }
    };

    const handleBulkPublic = async (isPublic) => {
        const selected = getSelectedLinks();
        if (selected.length === 0) { showSnackbar('Select at least one link.', 'warning'); return; }
        if (isPublic) {
            const untrained = selected.filter(l => !l.is_trained);
            if (untrained.length > 0) { showSnackbar(`${untrained.length} link(s) must be scraped and trained first.`, 'error'); return; }
        }
        if (!window.confirm(`Mark ${selected.length} link(s) as ${isPublic ? 'public' : 'private'}?`)) return;
        try {
            await axiosInstance.post('/employee-website/is-public', { link_ids: selected.map(l => l.id), is_public: isPublic });
            showSnackbar(`Links marked as ${isPublic ? 'public' : 'private'}!`);
            setSelectedIds(new Set()); loadLinks();
        } catch { showSnackbar('Failed to update.', 'error'); }
    };

    const closeProcessingModal = () => {
        setProcessingModal({ open: false, files: [], action: 'train' });
        setSelectedIds(new Set()); loadLinks();
    };

    // Export
    const handleExport = () => {
        if (links.length === 0) { showSnackbar('No links to export.', 'warning'); return; }
        const csv = links.map(l =>
            `"${l.website_link}","${l.is_scrape ? 'Scraped' : 'Not Scraped'}","${l.is_trained ? 'Trained' : 'Not Trained'}","${l.scrape_status}","${l.page_words}","${l.tokens}"`
        ).join('\n');
        const blob = new Blob([`URL,Scrape Status,Training Status,Status,Words,Tokens\n${csv}`], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `website-links-${activeTab}-${new Date().toISOString().split('T')[0]}.csv`;
        a.click(); window.URL.revokeObjectURL(url);
        showSnackbar(`Exported ${links.length} link(s) to CSV.`);
    };

    const isRecentlyAdded = (d) => d && new Date(d).getTime() > Date.now() - 86400000;

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: { xs: '1.5rem', md: '2rem' } }}>
                        Website Links
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily, color: '#64748b', mt: 0.5 }}>
                        Manage your personal website links for chatbot training
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button startIcon={<AddIcon />} variant="contained"
                        onClick={(e) => setAddMenuAnchor(e.currentTarget)}
                        endIcon={<KeyboardArrowDownIcon />}
                        sx={{ fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '10px', textTransform: 'none', '&:hover': { bgcolor: '#059669' } }}>
                        Add
                    </Button>
                    <Menu anchorEl={addMenuAnchor} open={Boolean(addMenuAnchor)} onClose={() => setAddMenuAnchor(null)}
                        PaperProps={{ sx: { borderRadius: '12px', minWidth: 200 } }}>
                        <MenuItem onClick={() => { setAddMenuAnchor(null); setAddLinkOpen(true); }}>
                            <ListItemIcon><LinkIcon fontSize="small" sx={{ color: GREEN_ACCENT }} /></ListItemIcon>
                            <ListItemText primaryTypographyProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}>Add Link</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => { setAddMenuAnchor(null); setBulkAddOpen(true); }}>
                            <ListItemIcon><ContentCopyIcon fontSize="small" sx={{ color: '#8b5cf6' }} /></ListItemIcon>
                            <ListItemText primaryTypographyProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}>Bulk Add</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => { setAddMenuAnchor(null); setSitemapMode('homepage'); setSitemapOpen(true); }}>
                            <ListItemIcon><HomeIcon fontSize="small" sx={{ color: '#6366f1' }} /></ListItemIcon>
                            <ListItemText primaryTypographyProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}>Crawl Homepage</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => { setAddMenuAnchor(null); setSitemapMode('sitemap'); setSitemapOpen(true); }}>
                            <ListItemIcon><MapIcon fontSize="small" sx={{ color: '#0ea5e9' }} /></ListItemIcon>
                            <ListItemText primaryTypographyProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}>Add Sitemap</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => { setAddMenuAnchor(null); setSitemapMode('list'); setSitemapOpen(true); }}>
                            <ListItemIcon><ListIcon fontSize="small" sx={{ color: '#f59e0b' }} /></ListItemIcon>
                            <ListItemText primaryTypographyProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}>List Sitemaps</ListItemText>
                        </MenuItem>
                    </Menu>
                    <Tooltip title="Export to CSV">
                        <IconButton onClick={handleExport} disabled={links.length === 0} sx={{ color: '#64748b' }}>
                            <DownloadIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Refresh">
                        <IconButton onClick={loadLinks} sx={{ color: '#64748b' }}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontFamily }}>{error}</Alert>}

            {/* Tabs */}
            <Tabs
                value={activeTab} onChange={(_, v) => { setActiveTab(v); setPage(0); setSelectedIds(new Set()); setExpandedId(null); }}
                sx={{
                    mb: 3, '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none', minHeight: 44, fontSize: '0.85rem' },
                    '& .Mui-selected': { color: `${GREEN_ACCENT} !important` },
                    '& .MuiTabs-indicator': { bgcolor: GREEN_ACCENT },
                }}
            >
                {TAB_CONFIG.map(t => <Tab key={t.value} value={t.value} label={t.label} />)}
            </Tabs>

            {/* Search */}
            <Box sx={{ mb: 2 }}>
                <TextField
                    size="small" placeholder="Search website links..." value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} /></InputAdornment> }}
                    sx={{ width: { xs: '100%', sm: 350 }, '& .MuiInputBase-root': { fontFamily, borderRadius: '10px', fontSize: '0.85rem' } }}
                />
            </Box>

            {/* Bulk Actions */}
            {selectedIds.size > 0 && (
                <Paper sx={{ p: 2, mb: 2, borderRadius: '12px', bgcolor: '#f0fdf4', border: `1px solid ${GREEN_ACCENT}20` }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, color: GREEN_ACCENT, fontSize: '0.85rem' }}>
                            {selectedIds.size} selected
                        </Typography>
                        {activeTab !== 'archived' && (
                            <>
                                {(activeTab === 'untrain' || activeTab === 'error') && (
                                    <Button size="small" variant="contained" onClick={() => handleBulkAction('scrape')}
                                        sx={{ fontFamily, fontWeight: 600, bgcolor: '#3b82f6', borderRadius: '8px', textTransform: 'none', fontSize: '0.75rem', '&:hover': { bgcolor: '#2563eb' } }}>
                                        Scrape
                                    </Button>
                                )}
                                {(activeTab === 'untrain' || activeTab === 'error' || activeTab === 'trained') && (
                                    <>
                                        <Button size="small" variant="contained" onClick={() => handleBulkAction('train')}
                                            sx={{ fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '8px', textTransform: 'none', fontSize: '0.75rem', '&:hover': { bgcolor: '#059669' } }}>
                                            Train
                                        </Button>
                                        <Button size="small" variant="contained" onClick={() => handleBulkAction('untrain')}
                                            sx={{ fontFamily, fontWeight: 600, bgcolor: '#f97316', borderRadius: '8px', textTransform: 'none', fontSize: '0.75rem', '&:hover': { bgcolor: '#ea580c' } }}>
                                            Untrain
                                        </Button>
                                    </>
                                )}
                                {activeTab === 'trained' && (
                                    <>
                                        <Button size="small" variant="contained" onClick={() => handleBulkPublic(true)}
                                            startIcon={<PublicIcon sx={{ fontSize: 16 }} />}
                                            sx={{ fontFamily, fontWeight: 600, bgcolor: '#3b82f6', borderRadius: '8px', textTransform: 'none', fontSize: '0.75rem', '&:hover': { bgcolor: '#2563eb' } }}>
                                            Public
                                        </Button>
                                        <Button size="small" variant="contained" onClick={() => handleBulkPublic(false)}
                                            startIcon={<LockIcon sx={{ fontSize: 16 }} />}
                                            sx={{ fontFamily, fontWeight: 600, bgcolor: '#64748b', borderRadius: '8px', textTransform: 'none', fontSize: '0.75rem', '&:hover': { bgcolor: '#475569' } }}>
                                            Private
                                        </Button>
                                    </>
                                )}
                                <Button size="small" variant="contained" onClick={() => handleBulkAction('archive')}
                                    startIcon={<ArchiveIcon sx={{ fontSize: 16 }} />}
                                    sx={{ fontFamily, fontWeight: 600, bgcolor: '#eab308', borderRadius: '8px', textTransform: 'none', fontSize: '0.75rem', '&:hover': { bgcolor: '#ca8a04' } }}>
                                    Archive
                                </Button>
                            </>
                        )}
                        {activeTab === 'archived' && (
                            <Button size="small" variant="contained" onClick={() => handleBulkAction('retrain')}
                                startIcon={<UnarchiveIcon sx={{ fontSize: 16 }} />}
                                sx={{ fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '8px', textTransform: 'none', fontSize: '0.75rem', '&:hover': { bgcolor: '#059669' } }}>
                                Retrain
                            </Button>
                        )}
                        <Button size="small" variant="contained" onClick={handleBulkDelete}
                            startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                            sx={{ fontFamily, fontWeight: 600, bgcolor: '#ef4444', borderRadius: '8px', textTransform: 'none', fontSize: '0.75rem', '&:hover': { bgcolor: '#dc2626' } }}>
                            Delete
                        </Button>
                        <Button size="small" onClick={() => setSelectedIds(new Set())}
                            sx={{ fontFamily, fontWeight: 600, color: GREEN_ACCENT, textTransform: 'none', fontSize: '0.75rem' }}>
                            Clear
                        </Button>
                    </Box>
                </Paper>
            )}

            {/* Table */}
            <Paper sx={{ borderRadius: '16px', border: '1px solid', borderColor: 'grey.100', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                <TableCell padding="checkbox">
                                    <Checkbox checked={allSelected} indeterminate={someSelected} onChange={handleSelectAll} size="small"
                                        sx={{ '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: GREEN_ACCENT } }} />
                                </TableCell>
                                {activeTab === 'trained' && <TableCell sx={{ width: 40 }} />}
                                {['Website URL', 'Status', ...(activeTab === 'trained' ? ['Visibility'] : []), 'Metrics', 'Auto Sync', 'Actions'].map(h => (
                                    <TableCell key={h} sx={{ fontFamily, fontWeight: 700, color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={activeTab === 'trained' ? 8 : 7} sx={{ textAlign: 'center', py: 6 }}>
                                        <CircularProgress size={32} sx={{ color: GREEN_ACCENT }} />
                                    </TableCell>
                                </TableRow>
                            ) : links.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={activeTab === 'trained' ? 8 : 7} sx={{ textAlign: 'center', py: 6 }}>
                                        <LanguageIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                        <Typography sx={{ fontFamily, color: '#94a3b8', mb: 1 }}>
                                            {searchTerm ? 'No links found' :
                                                activeTab === 'unscrape' ? 'No pending links' :
                                                activeTab === 'error' ? 'No error links' :
                                                activeTab === 'trained' ? 'No trained links yet' :
                                                'No archived links'}
                                        </Typography>
                                        {!searchTerm && activeTab === 'unscrape' && (
                                            <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setAddLinkOpen(true)}
                                                sx={{ fontFamily, fontWeight: 600, borderColor: GREEN_ACCENT, color: GREEN_ACCENT, borderRadius: '10px', textTransform: 'none', '&:hover': { borderColor: '#059669', bgcolor: `${GREEN_ACCENT}08` } }}>
                                                Add Your First Link
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ) : links.map(link => (
                                <React.Fragment key={link.id}>
                                    <TableRow sx={{ '&:hover': { bgcolor: '#f8fafc' }, ...(selectedIds.has(link.id) && { bgcolor: '#f0fdf4' }) }}>
                                        <TableCell padding="checkbox">
                                            <Checkbox checked={selectedIds.has(link.id)} onChange={() => handleSelectRow(link.id)} size="small"
                                                sx={{ '&.Mui-checked': { color: GREEN_ACCENT } }} />
                                        </TableCell>
                                        {activeTab === 'trained' && (
                                            <TableCell sx={{ width: 40, p: 0.5 }}>
                                                <IconButton size="small" onClick={() => setExpandedId(expandedId === link.id ? null : link.id)}>
                                                    {expandedId === link.id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                </IconButton>
                                            </TableCell>
                                        )}
                                        {/* URL */}
                                        <TableCell sx={{ maxWidth: 320 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <LanguageIcon sx={{ fontSize: 18, color: '#94a3b8', flexShrink: 0 }} />
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Typography variant="body2" sx={{ fontFamily, fontWeight: 600, color: GREEN_ACCENT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                                            onClick={() => window.open(link.website_link, '_blank', 'noopener,noreferrer')}>
                                                            {link.website_link}
                                                        </Typography>
                                                        {isRecentlyAdded(link.created_date) && (
                                                            <Chip label="New" size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.65rem', height: 18, bgcolor: '#dbeafe', color: '#2563eb' }} />
                                                        )}
                                                    </Box>
                                                    {link.title_tag && (
                                                        <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {link.title_tag}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        {/* Status */}
                                        <TableCell>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                {link.is_scrape ? (
                                                    <Chip label="Scraped" size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', bgcolor: '#d1fae5', color: '#059669' }} />
                                                ) : (
                                                    <Chip label={link.scrape_status === 'pending' ? 'Pending' : 'Not Scraped'} size="small"
                                                        sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', bgcolor: '#fef3c7', color: '#d97706' }} />
                                                )}
                                                {link.is_trained && (
                                                    <Chip label="Trained" size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', bgcolor: '#dbeafe', color: '#2563eb' }} />
                                                )}
                                                {link.has_pending_update && (
                                                    <Chip label="Update Pending" size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', bgcolor: '#ffedd5', color: '#ea580c' }} />
                                                )}
                                                {link.scrape_status === 'error' && (
                                                    <Tooltip title={link.scrape_error_message || 'Error occurred'}>
                                                        <Chip label="Error" size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', bgcolor: '#fee2e2', color: '#dc2626', cursor: 'help' }} />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                        {/* Visibility (trained tab only) */}
                                        {activeTab === 'trained' && (
                                            <TableCell>
                                                <Chip
                                                    icon={link.is_public ? <PublicIcon sx={{ fontSize: 14 }} /> : <LockIcon sx={{ fontSize: 14 }} />}
                                                    label={link.is_public ? 'Public' : 'Private'} size="small"
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', bgcolor: link.is_public ? '#dbeafe' : '#f1f5f9', color: link.is_public ? '#2563eb' : '#64748b' }}
                                                />
                                            </TableCell>
                                        )}
                                        {/* Metrics */}
                                        <TableCell>
                                            <Typography variant="caption" sx={{ fontFamily, color: '#64748b', display: 'block' }}>{link.page_words || 0} words</Typography>
                                            <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', display: 'block' }}>{link.tokens || 0} tokens</Typography>
                                        </TableCell>
                                        {/* Auto Sync */}
                                        <TableCell>
                                            {link.is_auto_sync ? (
                                                <Typography variant="caption" sx={{ fontFamily, color: '#059669', fontWeight: 600 }}>
                                                    Every {link.sync_interval_days || link.default_interval_days || 7}d
                                                </Typography>
                                            ) : (
                                                <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>Off</Typography>
                                            )}
                                        </TableCell>
                                        {/* Actions */}
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                {link.is_scrape && (
                                                    <Tooltip title="Edit Content">
                                                        <IconButton size="small" onClick={() => setEditLinkId(link.id)} sx={{ color: '#3b82f6' }}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Open in browser">
                                                    <IconButton size="small" onClick={() => window.open(link.website_link, '_blank', 'noopener,noreferrer')} sx={{ color: GREEN_ACCENT }}>
                                                        <OpenInNewIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                {link.is_scrape && (
                                                    <>
                                                        <Tooltip title="Refresh Data">
                                                            <IconButton size="small" onClick={() => handleRefreshData(link)} sx={{ color: '#059669' }}>
                                                                <RefreshIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        {link.page_content && (
                                                            <Tooltip title="Refresh Summary">
                                                                <IconButton size="small" onClick={() => handleRefreshSummary(link)} sx={{ color: '#8b5cf6' }}>
                                                                    <AutoAwesomeIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </>
                                                )}
                                                <Tooltip title="Sync Settings">
                                                    <IconButton size="small" onClick={() => setSyncLinkId(link.id)} sx={{ color: '#f97316' }}>
                                                        <SettingsIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                {link.scrape_status === 'error' && link.scrape_error_message && (
                                                    <Tooltip title={link.scrape_error_message}>
                                                        <IconButton size="small" sx={{ color: '#ef4444' }}>
                                                            <ErrorOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Delete">
                                                    <IconButton size="small" onClick={() => handleDeleteLink(link)} sx={{ color: '#ef4444' }}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    {/* Expanded Row for trained tab */}
                                    {activeTab === 'trained' && expandedId === link.id && (
                                        <TableRow>
                                            <TableCell colSpan={8} sx={{ p: 0, borderBottom: '1px solid #e2e8f0' }}>
                                                <ExpandedRow link={link} />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {activeTab !== 'archived' && (
                    <TablePagination
                        component="div" count={totalLinks} page={page} rowsPerPage={rowsPerPage}
                        onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[10, 25, 50]}
                        sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily, fontSize: '0.8rem' } }}
                    />
                )}
            </Paper>

            {/* Dialogs */}
            <AddLinkDialog open={addLinkOpen} onClose={() => setAddLinkOpen(false)} onSuccess={loadLinks} showSnackbar={showSnackbar} />
            <BulkAddDialog open={bulkAddOpen} onClose={() => setBulkAddOpen(false)} onSuccess={loadLinks} showSnackbar={showSnackbar} />
            <SitemapDialog open={sitemapOpen} onClose={() => setSitemapOpen(false)} onSuccess={loadLinks} showSnackbar={showSnackbar} mode={sitemapMode} />
            {editLinkId && <EditContentDialog open={!!editLinkId} onClose={() => setEditLinkId(null)} linkId={editLinkId} onSuccess={loadLinks} showSnackbar={showSnackbar} />}
            {syncLinkId && <SyncSettingsDialog open={!!syncLinkId} onClose={() => setSyncLinkId(null)} linkId={syncLinkId} onSuccess={loadLinks} showSnackbar={showSnackbar} />}
            {processingModal.open && (
                <ProcessingDialog
                    open={processingModal.open} onClose={closeProcessingModal}
                    files={processingModal.files} action={processingModal.action} showSnackbar={showSnackbar}
                />
            )}

            {/* Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ fontFamily, borderRadius: '10px' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
